import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import pb from '@/lib/pocketbase'
import type { KegiatanPeserta } from '@/types/pocketbase'

export type StatusPresensi = 'hadir' | 'alpha' | 'izin'

export interface PresensiRecord {
  id: string
  peserta_id: string
  kegiatan_id: string
  status: StatusPresensi
  ttd_file?: string
  checked_at: string
  checked_by: string
}

export interface PesertaWithPresensi extends KegiatanPeserta {
  presensi?: PresensiRecord
}

async function fetchPresensiList(kegiatanId: string) {
  const result = await pb.collection('presensi').getFullList({
    filter: `kegiatan_id = "${kegiatanId}"`,
  })
  return result as unknown as PresensiRecord[]
}

async function fetchPesertaWithPresensi(kegiatanId: string) {
  const [peserta, presensiRecords] = await Promise.all([
    pb.collection('kegiatan_peserta').getFullList({
      filter: `kegiatan = "${kegiatanId}" && status_pendaftaran = "diterima"`,
      expand: 'daerah_id,jabatan_id',
      sort: 'nama',
    }),
    pb.collection('presensi').getFullList({
      filter: `kegiatan_id = "${kegiatanId}"`,
    }),
  ])

  const presensiMap = new Map(presensiRecords.map((p) => [p.peserta_id, p as unknown as PresensiRecord]))
  return (peserta as unknown as KegiatanPeserta[]).map((p) => ({
    ...p,
    presensi: presensiMap.get(p.id),
  }))
}

async function fetchStatistikPresensi(kegiatanId: string) {
  const records = await pb.collection('presensi').getFullList({
    filter: `kegiatan_id = "${kegiatanId}"`,
  })
  const presensi = records as unknown as PresensiRecord[]
  const count = (status: StatusPresensi) => presensi.filter((p) => p.status === status).length
  return {
    hadir: count('hadir'),
    alpha: count('alpha'),
    izin: count('izin'),
    total: presensi.length,
  }
}

export function usePesertaWithPresensi(kegiatanId: string | undefined) {
  return useQuery({
    queryKey: ['presensi', 'peserta', kegiatanId],
    queryFn: () => fetchPesertaWithPresensi(kegiatanId!),
    enabled: !!kegiatanId,
  })
}

export function useStatistikPresensi(kegiatanId: string | undefined) {
  return useQuery({
    queryKey: ['presensi', 'statistik', kegiatanId],
    queryFn: () => fetchStatistikPresensi(kegiatanId!),
    enabled: !!kegiatanId,
    refetchInterval: 30000,
  })
}

export function usePresensiUpdate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      pesertaId,
      kegiatanId,
      status,
      ttdFile,
    }: {
      pesertaId: string
      kegiatanId: string
      status: StatusPresensi
      ttdFile?: File
    }) => {
      const formData = new FormData()
      formData.append('peserta_id', pesertaId)
      formData.append('kegiatan_id', kegiatanId)
      formData.append('status', status)
      formData.append('checked_at', new Date().toISOString())
      formData.append('checked_by', pb.authStore.model?.id || '')
      if (ttdFile) formData.append('ttd_file', ttdFile)

      const existing = await pb.collection('presensi').getFirstListItem(
        `peserta_id = "${pesertaId}" && kegiatan_id = "${kegiatanId}"`
      )
      const record = await pb.collection('presensi').update(existing.id, formData)
      return record
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['presensi', 'peserta', vars.kegiatanId] })
      qc.invalidateQueries({ queryKey: ['presensi', 'statistik', vars.kegiatanId] })
    },
  })
}

export function usePresensiBulkUpdate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      updates,
      kegiatanId,
    }: {
      updates: Array<{ pesertaId: string; status: StatusPresensi; ttdFile?: File }>
      kegiatanId: string
    }) => {
      const results = await Promise.allSettled(
        updates.map((u) =>
          (async () => {
            const formData = new FormData()
            formData.append('peserta_id', u.pesertaId)
            formData.append('kegiatan_id', kegiatanId)
            formData.append('status', u.status)
            formData.append('checked_at', new Date().toISOString())
            formData.append('checked_by', pb.authStore.model?.id || '')
            if (u.ttdFile) formData.append('ttd_file', u.ttdFile)

            try {
              const existing = await pb.collection('presensi').getFirstListItem(
                `peserta_id = "${u.pesertaId}" && kegiatan_id = "${kegiatanId}"`
              )
              return pb.collection('presensi').update(existing.id, formData)
            } catch {
              return pb.collection('presensi').create(formData)
            }
          })()
        )
      )
      return results
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['presensi', 'peserta', vars.kegiatanId] })
      qc.invalidateQueries({ queryKey: ['presensi', 'statistik', vars.kegiatanId] })
    },
  })
}
